import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography, Snackbar, Alert } from '@mui/material';
import { useUser } from '@clerk/clerk-react';
import theme from './theme.js';
import Header from './components/Navigation/Header.js';
import WizardPage from './pages/WizardPage.js';
import CharactersPage from './pages/CharactersPage.js';
import StoryDisplayPage from './pages/StoryDisplayPage.js';
import StoryLibraryPage from './pages/StoryLibraryPage.js';
import ProtectedRoute from './components/Auth/ProtectedRoute.js';
import LandingPage from './pages/LandingPage/LandingPage.js';
import PricingPage from './pages/PricingPage.js';
import AdminPage from './pages/AdminPage.js';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary.js';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner.js';
import { SubscriptionProvider } from './contexts/SubscriptionContext.js';
import './styles/App.css';
import { db, firebaseConfigError } from './lib/firebase.js';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { getDesignTokens } from './theme.js';

// Helper component to handle conditional header rendering
const ConditionalHeader = ({ mode, setMode }) => {
  const location = useLocation();
  // Don't show header on the landing page
  if (location.pathname === '/') {
    return null;
  }
  return <Header mode={mode} setMode={setMode} />;
};

// Wrapper for main content area to access location for conditional padding
const MainContent = ({ children }) => {
  const location = useLocation();
  // Determine horizontal padding based on route
  const horizontalPadding = location.pathname === '/' ? 0 : { xs: 1, sm: 2, md: 3 };
  // Define the header height (adjust if your header height is different)
  const headerHeight = '64px'; 

  return (
    <Box 
      component="main" 
      sx={{
        flexGrow: 1, 
        px: horizontalPadding, // Apply horizontal padding
        pt: location.pathname === '/' ? 0 : headerHeight, // Apply top padding ONLY if header is visible
      }}
    >
      {children}
    </Box>
  );
};

function AppInternal() {
  const [mode, setMode] = useState('light');
  const [characters, setCharacters] = useState([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Use Clerk for authentication
  const { user, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const location = useLocation();

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Correctly use the imported theme configuration function
  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // --- Data Fetching ---
  const fetchCharacters = useCallback(async (userId) => {
    if (firebaseConfigError) {
        setCharacters([]);
        setLoadingCharacters(false);
        return;
    }
    if (!userId) {
        setCharacters([]); // Clear characters if no user
        setLoadingCharacters(false);
        return;
    }

    setLoadingCharacters(true);
    let fetchedData = []; // To store data fetched initially

    try {
      // Fetch ONLY characters belonging to the logged-in user
      const q = query(
        collection(db, 'characters'),
        where('user_id', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      fetchedData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.name || '').localeCompare(String(b.name || '')));

      // Process and set state with the fetched data
      const processedData = fetchedData.map(char => ({
        ...char,
        traits: ensureArrayField(char.traits),
        specialAbility: ensureArrayField(char.specialAbility),
        likes: ensureArrayField(char.likes),
        dislikes: ensureArrayField(char.dislikes),
        fears: ensureArrayField(char.fears || char.additionalFields?.Fears),
        dreams: ensureArrayField(char.dreams || char.additionalFields?.Dreams),
        additionalFields: char.additionalFields || {},
        relationships: char.relationships || [],
        seed: char.seed || null
      }));

      setCharacters(processedData);

    } catch (error) {
      console.error('Error fetching characters:', error);
      setCharacters([]);
    } finally {
      setLoadingCharacters(false);
      setHasLoadedInitial(true); // Mark initial load complete
    }
  }, []); // useCallback dependency

  // --- Fetch characters when Clerk user changes ---
  useEffect(() => {
    if (isUserLoaded) {
      if (isSignedIn && user?.id) {
        fetchCharacters(user.id);
      } else {
        setCharacters([]);
        setLoadingCharacters(false);
        setHasLoadedInitial(true);
      }
    }
  }, [isUserLoaded, isSignedIn, user?.id, fetchCharacters]);

  // Updated helper - Detect and flatten ['["a","b"]'] pattern
  const ensureArrayField = (value) => {
    if (Array.isArray(value)) {
        if (value.length === 1 && typeof value[0] === 'string') {
            const innerString = value[0].trim();
            if (innerString.startsWith('[') && innerString.endsWith(']')) {
                try {
                    const parsed = JSON.parse(innerString);
                    return Array.isArray(parsed) ? parsed.map(item => String(item)) : [];
                } catch (e) {
                    return [];
                }
            }
        }
        return value.map(item => String(item)); 
    }
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmedValue);
                return Array.isArray(parsed) ? parsed.map(item => String(item)) : [];
            } catch (e) {
                return [];
            }
        } else {
            return [];
        }
    }
    return [];
  }

  // --- CRUD Operations ---
  const addCharacter = async (newCharacterData) => {
    if (!isSignedIn || !user?.id) {
      showNotification('You must be logged in to save characters.', 'warning');
      return;
    }
    const { id, ...dataToInsert } = newCharacterData;
    const characterToSave = {
      ...dataToInsert,
      user_id: user.id,
      traits: ensureArrayField(newCharacterData.traits),
      specialAbility: ensureArrayField(newCharacterData.specialAbility),
      likes: ensureArrayField(newCharacterData.likes),
      dislikes: ensureArrayField(newCharacterData.dislikes),
      fears: ensureArrayField(newCharacterData.additionalFields?.Fears),
      dreams: ensureArrayField(newCharacterData.additionalFields?.Dreams),
      additionalFields: newCharacterData.additionalFields || {},
      relationships: newCharacterData.relationships || [],
      seed: newCharacterData.seed || null
    };
    try {
      const docRef = await addDoc(collection(db, 'characters'), {
        ...characterToSave,
        created_at: new Date().toISOString()
      });
      const data = { id: docRef.id, ...characterToSave, created_at: new Date().toISOString() };
      const processedData = {
        ...data,
        traits: ensureArrayField(data?.traits),
        specialAbility: ensureArrayField(data?.specialAbility),
        likes: ensureArrayField(data?.likes),
        dislikes: ensureArrayField(data?.dislikes),
        fears: ensureArrayField(data?.fears || data?.additionalFields?.Fears),
        dreams: ensureArrayField(data?.dreams || data?.additionalFields?.Dreams),
        additionalFields: data.additionalFields || {},
        relationships: data.relationships || [],
        seed: data.seed || null
      };
      setCharacters(prevCharacters => [...prevCharacters, processedData]);
      showNotification('Character created successfully!', 'success');
    } catch (error) {
      showNotification(`Failed to add character: ${error.message}`, 'error');
    }
  };

  const updateCharacter = async (id, updatedCharacterData) => {
    if (!isSignedIn || !user?.id) {
      showNotification('You must be logged in to save characters.', 'warning');
      return;
    }
    if (!id) {
        return;
    }
    const { user_id, id: characterId, ...dataToUpdate } = updatedCharacterData;
     const characterToSave = {
      ...dataToUpdate,
      traits: ensureArrayField(updatedCharacterData.traits),
      specialAbility: ensureArrayField(updatedCharacterData.specialAbility),
      likes: ensureArrayField(updatedCharacterData.likes),
      dislikes: ensureArrayField(updatedCharacterData.dislikes),
      fears: ensureArrayField(updatedCharacterData.additionalFields?.Fears),
      dreams: ensureArrayField(updatedCharacterData.additionalFields?.Dreams),
      additionalFields: updatedCharacterData.additionalFields || {},
      relationships: updatedCharacterData.relationships || [],
      seed: updatedCharacterData.seed || null
    };
    try {
      const characterRef = doc(db, 'characters', id);
      await updateDoc(characterRef, characterToSave);
      const data = { id, ...characterToSave };
      const processedData = {
        ...data,
        traits: ensureArrayField(data?.traits),
        specialAbility: ensureArrayField(data?.specialAbility),
        likes: ensureArrayField(data?.likes),
        dislikes: ensureArrayField(data?.dislikes),
        fears: ensureArrayField(data?.fears || data?.additionalFields?.Fears),
        dreams: ensureArrayField(data?.dreams || data?.additionalFields?.Dreams),
        additionalFields: data.additionalFields || {},
        relationships: data.relationships || [],
        seed: data.seed || null
      };
      setCharacters(prevCharacters => prevCharacters.map(char =>
        char.id === id ? processedData : char
      ));
      showNotification('Character updated successfully!', 'success');
    } catch (error) {
      showNotification(`Failed to update character: ${error.message}`, 'error');
    }
  };

  const deleteCharacter = async (id) => {
    if (!isSignedIn || !user?.id) {
      showNotification('You must be logged in to delete characters.', 'warning');
      return;
    }
    if (!id) {
        return;
    }
    try {
        const characterRef = doc(db, 'characters', id);
        await deleteDoc(characterRef);
        setCharacters(prevCharacters => prevCharacters.filter(char => char.id !== id));
        showNotification('Character deleted successfully!', 'success');
    } catch (error) {
      showNotification(`Failed to delete character: ${error.message}`, 'error');
    }
  };

  if (firebaseConfigError && location.pathname !== '/') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Box sx={{ maxWidth: 640, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>App configuration incomplete</Typography>
            <Typography color="text.secondary">
              The app database is not configured for this deployment. Please contact the site administrator.
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Show loading while Clerk initializes
  if (!isUserLoaded) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <LoadingSpinner message="Loading..." />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SubscriptionProvider>
        <ErrorBoundary>
          <ConditionalHeader mode={mode} setMode={setMode} />
          <MainContent>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route
                path="/characters"
                element={
                  <ProtectedRoute>
                    <CharactersPage
                      characters={characters}
                      addCharacter={addCharacter}
                      updateCharacter={updateCharacter}
                      deleteCharacter={deleteCharacter}
                      isLoading={loadingCharacters}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wizard"
                element={
                  <ProtectedRoute>
                    {loadingCharacters && !hasLoadedInitial ? (
                      <LoadingSpinner message="Loading your characters..." />
                    ) : (
                      <WizardPage
                        characters={characters}
                        addCharacter={addCharacter}
                      />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route path="/story/:storyId" element={<StoryDisplayPage />} />
              <Route
                path="/library"
                element={
                  <ProtectedRoute>
                    <StoryLibraryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </MainContent>
        </ErrorBoundary>
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>
      </SubscriptionProvider>
    </ThemeProvider>
  );
}

// New App component to provide Router context
function App() {
  return (
    <Router>
      <AppInternal />
    </Router>
  );
}

export default App; // Export the new App component
